/**
 * UI Control API Service
 * 
 * Creates HTTP endpoints to allow the backend to control the React UI.
 * This service exposes the global solarFarmAPI via HTTP endpoints.
 */

export class UIControlAPIService {
  private isRunning = false;
  
  constructor() {
    this.setupAPI();
  }
  
  private setupAPI() {
    if (this.isRunning) return;
    this.isRunning = true;
    
    // Since React apps don't typically run HTTP servers, we'll use a different approach
    // We'll set up message listeners for the backend to communicate with
    this.setupMessageHandling();
  }
  
  private setupMessageHandling() {
    // Listen for messages from the backend (if using iframe communication)
    window.addEventListener('message', this.handleBackendMessage.bind(this));
    
    // Also expose direct API endpoints via global functions
    this.exposeAPIEndpoints();
  }
  
  private handleBackendMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) {
      // Only accept messages from same origin for security
      return;
    }
    
    const { type, data, id } = event.data;
    
    switch (type) {
      case 'UI_CONTROL_REQUEST':
        this.handleUIControlRequest(data, id);
        break;
      case 'UI_STATE_REQUEST':
        this.handleUIStateRequest(id);
        break;
    }
  }
  
  private handleUIControlRequest(data: any, requestId: string) {
    try {
      const api = (window as any).solarFarmAPI;
      if (!api) {
        this.sendResponse(requestId, { success: false, error: 'UI API not available' });
        return;
      }
      
      const { action, ...params } = data;
      let result;
      
      switch (action) {
        case 'navigate_to_tab':
          result = api.navigateToTab(params.tab_name);
          break;
        case 'select_components':
          result = api.selectComponents(params.component_ids);
          break;
        case 'scroll':
          result = api.scroll(params.direction, params.amount);
          break;
        case 'execute_javascript':
          result = api.executeJavaScript(params.code);
          break;
        default:
          result = { success: false, error: `Unknown action: ${action}` };
      }
      
      this.sendResponse(requestId, result);
    } catch (error) {
      this.sendResponse(requestId, { success: false, error: (error as Error).message });
    }
  }
  
  private handleUIStateRequest(requestId: string) {
    try {
      const api = (window as any).solarFarmAPI;
      if (!api) {
        this.sendResponse(requestId, { error: 'UI API not available' });
        return;
      }
      
      const state = api.getUIState();
      this.sendResponse(requestId, state);
    } catch (error) {
      this.sendResponse(requestId, { error: (error as Error).message });
    }
  }
  
  private sendResponse(requestId: string, data: any) {
    window.parent.postMessage({
      type: 'UI_CONTROL_RESPONSE',
      id: requestId,
      data
    }, window.location.origin);
  }
  
  private exposeAPIEndpoints() {
    // Expose simple global functions that the backend can call via JavaScript execution
    (window as any).backendAPI = {
      // Navigate to tab
      navigateToTab: (tabName: string) => {
        const api = (window as any).solarFarmAPI;
        return api ? api.navigateToTab(tabName) : { success: false, error: 'API not available' };
      },
      
      // Get UI state
      getUIState: () => {
        const api = (window as any).solarFarmAPI;
        return api ? api.getUIState() : { error: 'API not available' };
      },
      
      // Select components
      selectComponents: (componentIds: string[]) => {
        const api = (window as any).solarFarmAPI;
        return api ? api.selectComponents(componentIds) : { success: false, error: 'API not available' };
      },
      
      // Get component details
      getComponentDetails: (componentId: string) => {
        const api = (window as any).solarFarmAPI;
        return api ? api.getComponentDetails(componentId) : { error: 'API not available' };
      },
      
      // Execute script
      executeScript: (code: string) => {
        try {
          const result = eval(code);
          return { success: true, result };
        } catch (error) {
          return { success: false, error: (error as Error).message };
        }
      },
      
      // Scroll page
      scroll: (direction: string, amount: number) => {
        const api = (window as any).solarFarmAPI;
        return api ? api.scroll(direction, amount) : { success: false, error: 'API not available' };
      },
      
      // Get visible elements
      getVisibleElements: () => {
        const api = (window as any).solarFarmAPI;
        return api ? api.getVisibleElements() : { error: 'API not available' };
      }
    };
    
  }
  
  public cleanup() {
    this.isRunning = false;
    window.removeEventListener('message', this.handleBackendMessage.bind(this));
    delete (window as any).backendAPI;
  }
}

// Auto-initialize the service
let apiService: UIControlAPIService | null = null;

export function initializeUIControlAPI() {
  if (!apiService) {
    apiService = new UIControlAPIService();
  }
  return apiService;
}

export function cleanupUIControlAPI() {
  if (apiService) {
    apiService.cleanup();
    apiService = null;
  }
}